// ============================================================================
// geolocation.ts — УНИВЕРСАЛЬНЫЙ механизм сбора геокоординат (единая точка).
// Координаты — ЧУВСТВИТЕЛЬНЫЕ данные: используются только для отправки на
// сервер (LocationService/UploadFile), нигде не показываются по умолчанию.
// Явные ошибки (отказ/таймаут) — без тихих фоллбэков: вызов кидает Error.
// ============================================================================

export interface GeoPoint {
  lat: number;
  lon: number;
  // Точность определения (метры) — как вернул браузер (0, если недоступно)
  accuracy: number;
  // Время снятия точки (RFC3339, UTC)
  capturedAt: string;
}

export interface CaptureLocationOptions {
  /** Таймаут определения позиции, мс (default 10000) */
  timeoutMs?: number;
}

/**
 * Единая точка получения геопозиции клиента.
 * - доступ запрещён → Error('Доступ к геолокации запрещён')
 * - таймаут/недоступно → Error('Не удалось определить местоположение')
 * Никаких fallback'ов и «приблизительных» значений.
 */
export async function captureLocation(opts?: CaptureLocationOptions): Promise<GeoPoint> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    throw new Error('Геолокация не поддерживается');
  }
  const timeoutMs = opts?.timeoutMs ?? 10000;
  const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
            reject(new Error('Доступ к геолокации запрещён'));
            break;
          case err.POSITION_UNAVAILABLE:
            reject(new Error('Местоположение недоступно'));
            break;
          case err.TIMEOUT:
            reject(new Error('Не удалось определить местоположение (таймаут)'));
            break;
          default:
            reject(new Error('Ошибка геолокации'));
        }
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
  return {
    lat: pos.coords.latitude,
    lon: pos.coords.longitude,
    accuracy: pos.coords.accuracy || 0,
    capturedAt: new Date().toISOString(),
  };
}
